import { useState } from 'react';
import { CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import type { HashAlgo } from '../lib/hash';

const ALGO_LABEL: Record<HashAlgo, string> = {
  MD5: 'MD5',
  'SHA-1': 'SHA-1',
  'SHA-256': 'SHA-256',
  'SHA-384': 'SHA-384',
  'SHA-512': 'SHA-512',
};

const BROKEN: ReadonlySet<HashAlgo> = new Set(['MD5', 'SHA-1']);

/**
 * Single row in the Hash tool: `algo  ┃  hex/base64/base64url  ┃  copy`.
 * MD5 / SHA-1 carry a "broken" subscript — purely informational, the
 * tool still computes them because legacy parity is the whole point.
 */
export function HashRow({
  algo,
  value,
  pending,
}: {
  algo: HashAlgo;
  value: string | null;
  pending: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="grid grid-cols-[100px_1fr_auto] items-center gap-2 py-1">
      <div className="flex flex-col">
        <span className="font-mono text-[12px] text-ink">{ALGO_LABEL[algo]}</span>
        {BROKEN.has(algo) && (
          <span
            className="text-[10px] text-amber-600 dark:text-amber-400"
            title="不再适合安全用途；仅用于校验和或与遗留系统对接"
          >
            已不安全
          </span>
        )}
      </div>
      <code
        className={clsx(
          'min-w-0 break-all rounded bg-bg-soft px-2 py-1 font-mono text-[12px]',
          pending ? 'text-ink-dim' : 'text-ink',
        )}
      >
        {pending ? '— 计算中 —' : value === null ? '— 输入文本以计算 —' : value}
      </code>
      <button
        type="button"
        onClick={onCopy}
        disabled={!value}
        className="rounded p-1 text-ink-dim hover:bg-bg-soft hover:text-accent disabled:opacity-40"
        title="复制"
      >
        {copied ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
        ) : (
          <ClipboardCopy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}
